import React from 'react';

/**
 * The monospace identification block from the hero ring — a short stack of
 * tracked uppercase lines above whatever they annotate.
 *
 * This is the cheapest piece of the instrument language and the one that
 * carries most of its recognition. It is plain text: no SVG, no JavaScript.
 *
 * The hero sets some of these as small as 8px. Nothing here goes below 10px —
 * a label nobody can read is decoration, and decoration was never the point.
 */

export interface StampProps {
  /** Two or three short lines. The first is set brightest, as the reference. */
  lines: string[];
  /** Sitting on a dark band flips the palette to the on-ink tokens. */
  onDark?: boolean;
  align?: 'left' | 'right' | 'center';
  className?: string;
}

export default function Stamp({ lines, onDark, align = 'left', className }: StampProps) {
  const visible = lines.filter((line) => line && line.trim());
  if (!visible.length) return null;

  const channel = onDark ? 'var(--m-on-ink-rgb)' : 'var(--m-ink-rgb)';

  return (
    <div
      className={className}
      style={{
        textAlign: align,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        textTransform: 'uppercase',
      }}
    >
      {visible.map((line, i) => (
        <p
          key={line}
          style={{
            margin: 0,
            fontSize: i === 0 ? 11 : 10,
            lineHeight: 1.5,
            letterSpacing: '0.16em',
            // The first line is the reading; the rest is provenance.
            color: `rgba(${channel},${i === 0 ? 0.75 : 0.5})`,
          }}
        >
          {line}
        </p>
      ))}
    </div>
  );
}
